import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { toast } from "react-hot-toast";
import { hasValue } from "js/helpers/common_functions";

export const getTimeZoneOptions = () => {
    try {
        if (
            typeof Intl !== "undefined" &&
            typeof Intl.supportedValuesOf === "function"
        ) {
            const zones = Intl.supportedValuesOf("timeZone") || [];
            return ["UTC", ...zones.filter((z) => z !== "UTC")];
        }
    } catch (error) {
        console.warn("Failed to load full timezone list from Intl.supportedValuesOf:", error);
    }

    return [
        "UTC",
        "Australia/Adelaide",
        "Australia/Brisbane",
        "Australia/Darwin",
        "Australia/Hobart",
        "Australia/Melbourne",
        "Australia/Perth",
        "Australia/Sydney",
        "Asia/Bangkok",
        "Asia/Dubai",
        "Asia/Hong_Kong",
        "Asia/Kolkata",
        "Asia/Seoul",
        "Asia/Shanghai",
        "Asia/Singapore",
        "Asia/Tokyo",
        "Europe/Amsterdam",
        "Europe/Berlin",
        "Europe/London",
        "Europe/Madrid",
        "Europe/Paris",
        "America/Chicago",
        "America/Denver",
        "America/Los_Angeles",
        "America/New_York",
        "America/Phoenix",
        "America/Sao_Paulo",
        "Pacific/Auckland",
    ];
};

export const getBrowserTimeZone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
        return "UTC";
    }
};

export const parseUtcToTimeZoneParts = (timeString, timeZone = "UTC") => {
    if (!timeString) return null;

    const utcTimeString = timeString.endsWith("Z") ? timeString : `${timeString}Z`;
    const date = new Date(utcTimeString);

    if (isNaN(date.getTime())) return null;

    const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
    });

    const parts = formatter.formatToParts(date);
    const partMap = Object.fromEntries(parts.map((p) => [p.type, p.value]));

    return {
        year: Number(partMap.year),
        month: Number(partMap.month),
        day: Number(partMap.day),
        hour: Number(partMap.hour),
        minute: Number(partMap.minute),
        second: Number(partMap.second),
    };
};

export const localPartsToExcelSerial = ({ year, month, day, hour, minute, second }) => {
    const excelEpoch = Date.UTC(1899, 11, 30);
    const utcMillis = Date.UTC(year, month - 1, day, hour, minute, second);
    return (utcMillis - excelEpoch) / 86400000;
};

export const parseStartTimeToExcelSerial = (timeString, timeZone = "UTC") => {
    const parts = parseUtcToTimeZoneParts(timeString, timeZone);
    if (!parts) return null;
    return localPartsToExcelSerial(parts);
};

export const parseStartTimeToDateForCompare = (timeString) => {
    if (!timeString) return null;

    const utcTimeString = timeString.endsWith("Z") ? timeString : `${timeString}Z`;
    const date = new Date(utcTimeString);

    return isNaN(date.getTime()) ? null : date;
};

export const transformSingleDeviceCallLogs = (apiData, device) => {
    if (!Array.isArray(apiData)) return [];

    return apiData.map((call, index) => ({
        id: call.id || `${device.id}-call-${index + 1}`,
        rawId: call.id || null,
        deviceId: device.id,
        deviceName: device.name || "",
        serialNo: device.serialNo || "",
        startTime: call.createdAt || null,
        from: call.initiator || "Unknown",
        initiatorId: call.initiatorId || null,
        duration:
            call.callType === "Missed Call"
                ? "-"
                : call.callDuration || "Unknown",
        participants: call.participants || [],
        callType: call.callType || "",
        transcriptionFileName: call.transFileName || "",
        audioRecordingFileName: call.audioFileName || "",
    }));
};

export const getCallTypeLabel = (call) => {
    if (hasValue(call.callType)) {
        return call.callType;
    }

    if (call?.participants?.length > 0) {
        const hasPortalUserInitiator =
            call.participants.filter(
                (a) => a?.userId === call.initiatorId && a?.userId !== null
            ).length > 0;

        return hasPortalUserInitiator ? "Dashboard Call" : "Family Call";
    }

    return "Family Call";
};

export const getToParticipants = (call) => {
    if (!call?.participants?.length) return "";

    return call.participants
        .filter(
            (a) =>
                a?.deviceId !== call.initiatorId &&
                a?.userId !== call.initiatorId &&
                hasValue(a?.name)
        )
        .map((s) => s.name)
        .join(", ");
};

export const exportCallLogsToExcel = async (
    logsToExport,
    fileSuffix = "bulk_export",
    timeZone = "UTC"
) => {
    if (!logsToExport || logsToExport.length === 0) {
        toast.error("No data to export");
        return;
    }

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Call Logs");

    ws.columns = [
        { header: "Device name", key: "deviceName", width: 22 },
        { header: "Serial", key: "serial", width: 18 },
        { header: "Date and time", key: "dateTime", width: 22 },
        { header: "From", key: "from", width: 18 },
        { header: "To", key: "to", width: 30 },
        { header: "Type", key: "type", width: 16 },
        { header: "Duration", key: "duration", width: 12 },
    ];

    logsToExport.forEach((call) => {
        const dtSerial = parseStartTimeToExcelSerial(call.startTime, timeZone);

        ws.addRow({
            deviceName: call.deviceName || "",
            serial: call.serialNo || "",
            dateTime: dtSerial ?? "",
            from: call.from || "",
            to: getToParticipants(call),
            type: getCallTypeLabel(call),
            duration: call.duration || "",
        });
    });

    ws.getColumn("dateTime").numFmt = "m/d/yyyy h:mm AM/PM";

    ws.eachRow((row, rowNumber) => {
        row.eachCell((cell) => {
            cell.font = {
                name: "DengXian",
                size: 11,
                bold: rowNumber === 1,
            };
        });
    });

    const buffer = await wb.xlsx.writeBuffer();

    saveAs(
        new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `call_logs_${fileSuffix}.xlsx`
    );
};